"use client";

import { useTranslations } from "next-intl";
import { ButtonArrow } from "@/components/common/buttons/button-arrow";
import { Img } from "@/components/image";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import type { ExampleMenuType } from "@/features/static-builder/types/example-menu.type";
import { strapiUrl } from "@/lib/strapi";

export const ExampleMenu = ({ data }: { data: ExampleMenuType }) => {
  const t = useTranslations();

  const MEAL_LABELS = [
    t("staticBuilder.mealLabels.breakfast"),
    t("staticBuilder.mealLabels.second_breakfast"),
    t("staticBuilder.mealLabels.dinner"),
    t("staticBuilder.mealLabels.afternoon_snack"),
    t("staticBuilder.mealLabels.supper"),
  ] as const;

  return (
    <section className="py-6 max-md:container md:bg-brand-grey4 lg:py-22">
      <div className="md:container max-md:bg-brand-grey4 max-md:p-6">
        <div className="space-y-2.5">
          <p className="font-medium text-brand-violet uppercase">
            {data.subheading}
          </p>
          <h2 className="font-medium text-3.5xl text-brand-green2-300 leading-10 md:w-[60%] lg:text-5xl lg:leading-14 xl:w-[45%]">
            {data.heading}
          </h2>
          <p className="text-brand-grey5 leading-6 md:w-[50%]">
            {data.description}
          </p>
        </div>

        <Carousel className="mt-14" opts={{ align: "start", dragFree: true }}>
          <CarouselContent className="max-lg:last:mr-4">
            {data.menuItems.map((item, index) => {
              const image = item.image;
              const imageUrl = image?.url ? strapiUrl(image.url) : "";

              return (
                <CarouselItem
                  className="basis-60.75 lg:basis-1/5"
                  key={item.id}
                >
                  <div className="flex h-full flex-col overflow-hidden rounded-[20px] border border-brand-grey2 bg-brand-white">
                    <div className="relative h-40 w-full">
                      <Img
                        alt={image?.name || item.title}
                        className="h-full w-full object-cover"
                        height={image?.height}
                        src={imageUrl}
                        width={image?.width}
                      />
                    </div>
                    <div className="flex flex-1 flex-col gap-3 px-4.5 py-4">
                      <p className="font-medium text-brand-grey5 text-sm uppercase leading-4.5">
                        {MEAL_LABELS.at(index) ?? ""}
                      </p>
                      <div>
                        <h3 className="font-bold text-base text-brand-green2-300 leading-6">
                          {item.title}
                        </h3>
                        <p className="mt-1 text-base text-brand-grey5 leading-6">
                          {item.description}
                        </p>
                      </div>
                      <span className="mt-auto inline-flex w-fit items-center rounded-full bg-brand-grey4 px-3 py-1.5 font-medium text-brand-green2-300 text-xs leading-4.5">
                        {item.kcal}
                      </span>
                    </div>
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>
      </div>
      <ButtonArrow className="mt-12 w-full md:hidden" href="/diets">
        {t("header.orderDiet")}
      </ButtonArrow>
    </section>
  );
};
